import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CookieConsent = ({ userId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: false,
    performance: false
  });

  useEffect(() => {
    checkCookieConsent();
  }, [userId]);

  const checkCookieConsent = async () => {
    // Check localStorage first (for anonymous users)
    const localConsent = localStorage.getItem('cookieConsent');
    
    if (localConsent) {
      setIsVisible(false);
      return;
    }

    // If user has account, check backend
    if (userId) {
      try {
        const response = await axios.get(`${API}/legal/consent/${userId}`);
        if (response.data.cookie_consent) {
          setIsVisible(false);
          return;
        }
      } catch (error) {
        console.error('Error checking cookie consent:', error);
      }
    }

    // Show banner if no consent recorded
    // Wait 1 second before showing to avoid flash on page load
    setTimeout(() => setIsVisible(true), 1000);
  };

  const saveConsent = async (consentType) => {
    const consentData = {
      essential: true,
      analytics: consentType === 'all' ? true : preferences.analytics,
      performance: consentType === 'all' ? true : preferences.performance
    };

    // Save to localStorage
    localStorage.setItem('cookieConsent', consentType);
    localStorage.setItem('cookiePreferences', JSON.stringify(consentData));

    // If user has account, save to backend
    if (userId) {
      try {
        await axios.put(`${API}/legal/consent/${userId}`, {
          cookie_consent: consentType
        });
      } catch (error) {
        console.error('Error saving cookie consent:', error);
      }
    }

    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent('all');
  };

  const handleAcceptEssential = () => {
    saveConsent('essential');
  };

  const handleCustomize = () => {
    setShowDetails(!showDetails);
  };

  const handleSaveCustom = () => {
    const consentType = preferences.analytics || preferences.performance ? 'custom' : 'essential';
    saveConsent(consentType);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 right-0 z-40 p-4 md:p-6"
      >
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-3 border-ssdp-navy overflow-hidden">
            {/* Main Content */}
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-ssdp-teal rounded-full flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-black text-ssdp-navy dark:text-white mb-2 font-heading">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 font-body">
                    Pathways uses cookies to enhance your experience. Essential cookies keep you logged in and remember your preferences. 
                    Optional cookies help us understand how you use the platform to make it better.
                  </p>
                  
                  {/* Detailed Preferences */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mb-4 space-y-3"
                      >
                        {/* Essential Cookies */}
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-green-600" />
                              <span className="font-semibold text-sm text-ssdp-navy dark:text-white">Essential Cookies</span>
                              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-0.5 rounded-full">Always Active</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            Required for authentication, security, and core functionality.
                          </p>
                        </div>

                        {/* Analytics Cookies */}
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={preferences.analytics}
                                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                                  className="w-4 h-4 text-ssdp-teal rounded focus:ring-ssdp-teal"
                                />
                                <span className="font-semibold text-sm text-ssdp-navy dark:text-white">Analytics Cookies</span>
                              </label>
                              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 ml-6">
                                Help us understand which scenarios are most helpful.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Performance Cookies */}
                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={preferences.performance}
                                  onChange={(e) => setPreferences({...preferences, performance: e.target.checked})}
                                  className="w-4 h-4 text-ssdp-teal rounded focus:ring-ssdp-teal"
                                />
                                <span className="font-semibold text-sm text-ssdp-navy dark:text-white">Performance Cookies</span>
                              </label>
                              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 ml-6">
                                Improve loading speed and optimize performance.
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {!showDetails ? (
                      <>
                        <Button
                          onClick={handleAcceptAll}
                          className="bg-ssdp-teal text-white font-semibold hover:bg-ssdp-teal-hover shadow-sm font-heading"
                        >
                          Accept All
                        </Button>
                        <Button
                          onClick={handleAcceptEssential}
                          variant="outline"
                          className="border-2 border-ssdp-navy text-ssdp-navy dark:text-white font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 font-heading"
                        >
                          Essential Only
                        </Button>
                        <Button
                          onClick={handleCustomize}
                          variant="ghost"
                          className="text-ssdp-navy dark:text-white font-semibold flex items-center gap-2 font-heading"
                        >
                          <Settings className="w-4 h-4" />
                          Customize
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={handleSaveCustom}
                          className="bg-ssdp-teal text-white font-semibold hover:bg-ssdp-teal-hover shadow-sm font-heading"
                        >
                          Save Preferences
                        </Button>
                        <Button
                          onClick={handleCustomize}
                          variant="outline"
                          className="border-2 border-ssdp-navy text-ssdp-navy dark:text-white font-semibold font-heading"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    Learn more in our{' '}
                    <Link to="/legal/cookie-notice" className="text-ssdp-teal hover:underline">
                      Cookie Notice
                    </Link>
                    {' '}and{' '}
                    <Link to="/legal/privacy" className="text-ssdp-teal hover:underline">
                      Privacy Policy
                    </Link>
                  </p>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleAcceptEssential}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  aria-label="Accept essential cookies and close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
